import React, { useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { SERVER_BASE_URL } from "../Config";

interface UploadResponse {
  success: boolean;
  message: string;
  created_count?: number;
  providers_count?: number;
  removed_count?: number;
  errors?: string[];
}

const CustomPricingUpload: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!/\.(xlsx|xls)$/i.test(file.name)) {
        setUploadResult({
          success: false,
          message: "Please select a valid Excel file (.xlsx)"
        });
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/upload-custom-pricing`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${pharmacistToken}`,
        },
        body: formData,
      });

      const data: UploadResponse = await response.json();
      setUploadResult(data);
    } catch (error) {
      setUploadResult({
        success: false,
        message: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadResult(null);
  };

  return (
    <Paper sx={{ p: 3, flex: 1 }}>
      <Typography variant="h5" mb={2}>
        Custom Doctor Pricing
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={1}>
        Upload an Excel file to set a custom Provider Price per doctor. The custom price replaces the
        standard Provider Price everywhere for that doctor. Retail Price is not affected, and
        re-uploading the product workbook does not clear these prices.
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={1}>
        Read from the <strong>Prices</strong> sheet if the workbook has one, otherwise the first
        sheet. Required columns: <strong>Doctor Account, Product ID, Price</strong> &mdash; for
        example <code>test@provider.com, SEM_01, $80.00</code>
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        This file replaces <strong>all</strong> existing custom pricing. Any doctor or product not
        listed in the file loses its custom price and reverts to the standard Provider Price. If the
        file contains any errors, nothing is changed.
      </Alert>

      <Box mb={3}>
        <input
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          id="custom-pricing-file-upload"
          type="file"
          onChange={handleFileSelect}
        />
        <label htmlFor="custom-pricing-file-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<CloudUploadIcon />}
            fullWidth
            sx={{ mb: 2 }}
          >
            Select Excel File
          </Button>
        </label>

        {selectedFile && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Selected file: <strong>{selectedFile.name}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Size: {(selectedFile.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            fullWidth
          >
            {uploading ? 'Uploading...' : 'Upload and Replace Pricing'}
          </Button>

          {(selectedFile || uploadResult) && (
            <Button
              variant="outlined"
              onClick={resetUpload}
              disabled={uploading}
            >
              Reset
            </Button>
          )}
        </Box>
      </Box>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Validating spreadsheet and updating pricing...
          </Typography>
        </Box>
      )}

      {uploadResult && (
        <Alert
          severity={uploadResult.success ? "success" : "error"}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-line' }}>
            {uploadResult.message}
          </Typography>
        </Alert>
      )}

      {uploadResult?.success && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" mb={2}>Upload Statistics</Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Custom Prices Loaded"
                secondary={uploadResult.created_count ?? 0}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Doctor Accounts Affected"
                secondary={uploadResult.providers_count ?? 0}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Previous Prices Replaced"
                secondary={uploadResult.removed_count ?? 0}
              />
            </ListItem>
          </List>
        </Paper>
      )}

      {uploadResult && !uploadResult.success && uploadResult.errors && uploadResult.errors.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="error" mb={1}>
            Error Details (first {uploadResult.errors.length}):
          </Typography>
          {uploadResult.errors.map((error, index) => (
            <Typography key={index} variant="caption" color="error" display="block">
              {error}
            </Typography>
          ))}
        </Paper>
      )}
    </Paper>
  );
};

export default CustomPricingUpload;
